// Catalog browser — image gallery for the selected product.
//
// Stacks: primary catalog hero (large) + a horizontal strip of
// thumbnails for additionalImages + the LLM-judged ad-crop winners
// (5:4, 1:1, 4:5) of the catalog hero. Click a thumb to swap the
// main image. Empty state when no images at all.
//
// (Per-match YOLO crops moved OUT of the gallery — they belong to the
//  matched UGC posts, not the catalog hero, and were misleading the
//  operator into thinking they were judged crops of this product.)

import { useEffect, useState, useMemo } from 'react';
import { Box, HStack, VStack, Image, Text, Badge, Flex } from '@chakra-ui/react';
import type { CatalogDetail, HeroCrops } from './types';

type Props = {
  product:        CatalogDetail;
  heroCrops:      HeroCrops;
  altCrops:       HeroCrops[];
  activeAltIndex: number | null;     // null = parent hero is the gallery's primary
};

type GalleryEntry = {
  url:      string;
  kind:     'primary' | 'additional' | 'crop';
  label?:   string;
  source?:  string;
};

export function ImageGallery({ product, heroCrops, altCrops, activeAltIndex }: Props) {
  // When an alt is the gallery's active item (clicked from the left
  // rail alt-fanout), the "primary" tile becomes the alt's image and
  // the judged crops come from altCrops[index]. Otherwise we show the
  // parent hero + its own crops.
  const isAltActive = activeAltIndex != null;
  const activeAltUrl = isAltActive
    ? (product.additionalImages?.[activeAltIndex!] || null)
    : null;
  const activeCrops = isAltActive
    ? (altCrops?.[activeAltIndex!] || null)
    : heroCrops;
  const primaryUrl = isAltActive ? activeAltUrl : product.imageUrl;
  const primaryLabel = isAltActive
    ? `Alt ${activeAltIndex! + 1}`
    : 'Catalog hero';

  // THE FULL FEED SET, IN MERCHANT FEED ORDER: primary first, then the alts
  // in additionalImages order. Owner directive 2026-08-05: "any time you
  // display catalog images they should be shown in feed order, primary first,
  // then the alts." Never re-sort this by shotType, ad-suitability score or
  // createdAt — createdAt in particular is NOT feed order (the backend proved
  // heroes routinely materialise AFTER their alts).
  //
  // This strip used to contain the primary and the judged crops ONLY, despite
  // this file's own header comment promising additionalImages thumbnails and
  // GalleryEntry declaring a 'additional' kind that nothing ever produced —
  // alts were reachable only from the left rail.
  const feedEntries = useMemo<GalleryEntry[]>(() => {
    const out: GalleryEntry[] = [];
    if (product.imageUrl) {
      out.push({ url: product.imageUrl, kind: 'primary', label: 'Catalog hero' });
    }
    (product.additionalImages || []).forEach((url, i) => {
      // Skip empties and any alt that merely repeats the primary — the same
      // dedupe the left rail applies, so the two surfaces agree.
      if (!url || url === product.imageUrl) return;
      out.push({ url, kind: 'additional', label: `Alt ${i + 1}` });
    });
    return out;
  }, [product.imageUrl, product.additionalImages]);

  const entries = useMemo<GalleryEntry[]>(() => {
    const out: GalleryEntry[] = [...feedEntries];
    // LLM-judged ad-crop winners for whichever image is the gallery's
    // primary (parent hero OR the selected alt). These are the framings
    // the renderer will use when this image becomes a hero in an ad.
    // They trail the feed set — they are derived views, not catalog images.
    if (activeCrops) {
      for (const ratio of ['5:4', '1:1', '4:5'] as const) {
        const url = activeCrops[ratio];
        if (url) out.push({ url, kind: 'crop', label: `Judged ${ratio} crop` });
      }
    }
    // Degenerate catalog (no imageUrl and no usable alts) but an alt was
    // somehow selected: keep the old single-tile behaviour so the large
    // pane still renders something rather than going empty.
    if (!feedEntries.length && primaryUrl) {
      out.unshift({ url: primaryUrl, kind: 'primary', label: primaryLabel });
    }
    return out;
  }, [feedEntries, activeCrops, primaryUrl, primaryLabel]);

  const [activeIdx, setActiveIdx] = useState(0);
  // Point the large pane at whichever feed image the left rail selected.
  // The strip is always in feed order, so the selected alt's position IS its
  // index in that order — no separate "active" list to keep in sync.
  useEffect(() => {
    if (!isAltActive || !activeAltUrl) { setActiveIdx(0); return; }
    const i = feedEntries.findIndex(e => e.url === activeAltUrl);
    setActiveIdx(i >= 0 ? i : 0);
  }, [product.id, activeAltIndex, activeAltUrl, isAltActive, feedEntries]);

  const active = entries[activeIdx] || null;

  return (
    <Box
      flex={1}
      minH={0}
      bg="gray.50"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      <Flex flex={1} align="center" justify="center" minH={0} p={5}>
        {active ? (
          <Box
            position="relative"
            bg="gray.100"
            borderRadius="md"
            overflow="hidden"
            sx={{
              maxWidth: '100%',
              maxHeight: '100%',
              height: 'auto',
              width: 'auto'
            }}
          >
            <Image
              src={active.url}
              alt={active.label || 'product'}
              maxW="100%"
              maxH="62vh"
              objectFit="contain"
              display="block"
            />
            <Badge
              position="absolute"
              top={2}
              left={2}
              fontSize="9px"
              variant="subtle"
              colorScheme={active.kind === 'crop' ? 'purple' : active.kind === 'additional' ? 'gray' : 'blue'}
              px={2}
              py={0.5}
            >
              {active.label}{active.source ? ` · ${active.source}` : ''}
            </Badge>
          </Box>
        ) : (
          <VStack spacing={2}>
            <Text fontSize="md" fontWeight="700" color="brand.muted">No images</Text>
            <Text fontSize="xs" color="brand.muted" maxW="320px" textAlign="center">
              This product has no primary image, no additional images, and no per-match crops yet.
            </Text>
          </VStack>
        )}
      </Flex>

      {entries.length > 1 && (
        <HStack
          spacing={2}
          px={5}
          py={3}
          bg="brand.surface"
          borderTopWidth="1px"
          borderTopColor="brand.border"
          overflowX="auto"
        >
          {entries.map((e, i) => (
            <Thumb
              key={`${e.kind}-${i}`}
              entry={e}
              isActive={i === activeIdx}
              onClick={() => setActiveIdx(i)}
            />
          ))}
        </HStack>
      )}
    </Box>
  );
}

function Thumb({ entry, isActive, onClick }: { entry: GalleryEntry; isActive: boolean; onClick: () => void }) {
  const tone = entry.kind === 'crop' ? 'purple' : entry.kind === 'additional' ? 'gray' : 'blue';
  return (
    <Box flexShrink={0} cursor="pointer" onClick={onClick}>
      <Box
        w="80px" h="80px"
        borderRadius="md"
        overflow="hidden"
        borderWidth={isActive ? '2px' : '1px'}
        borderColor={isActive ? 'rsViolet.400' : 'brand.border'}
        bg="gray.100"
      >
        <Image src={entry.url} alt={entry.label || ''} w="100%" h="100%" objectFit="cover" />
      </Box>
      <Badge
        mt={1}
        fontSize="8px"
        variant="subtle"
        colorScheme={tone}
        textTransform="none"
        display="block"
        textAlign="center"
        px={1}
      >
        {entry.kind === 'crop' ? 'crop' : entry.kind === 'additional' ? 'alt' : 'main'}
      </Badge>
    </Box>
  );
}
